import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startGetAdsCampaign,
  startGetAdsCampaignRecordsByIDs,
  startGetAdsCampaigns,
  startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms,
  startGetHomeMetricsConfiguration,
  startSetHomeMetricsConfiguration,
  removeAdsCampaignStatusOverrides,
} from "../actions/adsCampaigns";
import { startGetAdsCampaignFolderContentIDs } from "../actions/adsCampaignFolders";
import { getPathDatabaseByRole } from "../actions/auth";
import { getStatusAdsCampaign } from "../services/adsCampaigns";
import {
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGNS_QUERY_PAGE_SIZE,
  ALERTS_ASYNC_STATUS,
  ALL_FILTER,
  DEFAULT_SORT_CONFIG,
  SORTABLE_COLUMNS,
  SORT_DIRECTION,
  DATE_RANGE,
} from "../utils/constants";

const useAdsCampaignsList = ({ folderID = ALL_FILTER } = {}) => {
  const dispatch = useDispatchApp();
  const { adsCampaigns, statusOverrides = {} } = useSelectorApp(
    (state) => state.adsCampaigns
  );
  const { alerts = [] } = useSelectorApp((state) => state.alerts);

  const publishingCampaigns = alerts.filter(
    (alert) => alert.type === "campaign"
  );

  const { userID } = dispatch(getPathDatabaseByRole());

  const [campaigns, setCampaigns] = useState(null);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [lastCampaignID, setLastCampaignID] = useState(null);
  const [loadedLastCampaign, setLoadedLastCampaign] = useState(false);
  const [campaignsSynced, setCampaignsSynced] = useState([]);
  const [syncingCampaignIDs, setSyncingCampaignIDs] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [subValueSort, setSubValueSort] = useState(null);
  const [datePresetKey, setDatePresetKey] = useState(null);
  const [savedDateRange, setSavedDateRange] = useState(null);
  const [folderIndex, setFolderIndex] = useState(null);
  const folderRequestRef = useRef(0);
  const loadedFolderIDsRef = useRef(new Set());
  const campaignsSyncedRef = useRef([]);
  const statusOverridesRef = useRef(statusOverrides);
  const folderCampaignsBaselineRef = useRef(adsCampaigns);

  const activeFolderID = folderID && folderID !== ALL_FILTER ? folderID : null;
  statusOverridesRef.current = statusOverrides;
  campaignsSyncedRef.current = campaignsSynced;

  const onUpdateAdsCampaignInformationFromPlatforms = useCallback(
    async (campaignIDs) => {
      const response = await dispatch(
        startUpdateAdsCampaignsStatusAndStatisticsFromPlatforms({
          campaignIDs,
        })
      );
      return response;
    },
    [dispatch]
  );

  // eslint-disable-next-line
  const folderCampaignIDs = !activeFolderID
    ? null
    : folderIndex?.folderID === activeFolderID
      ? folderIndex.ids
      : [];

  const applyOverrides = useCallback(
    (records) => {
      const overrides = statusOverridesRef.current || {};
      const overridesToClear = [];
      const data = {};

      Object.keys(records || {}).forEach((campaignID) => {
        const campaign = records[campaignID] || {};
        const statusCampaign = getStatusAdsCampaign({
          states: campaign.states,
          target: "general",
        });
        const overrideStatus = overrides[campaignID];
        data[campaignID] = overrideStatus
          ? {
              ...campaign,
              states: {
                ...campaign.states,
                general: overrideStatus,
              },
            }
          : campaign;

        if (overrideStatus && overrideStatus === statusCampaign) {
          overridesToClear.push(campaignID);
        }
      });

      if (overridesToClear.length > 0) {
        dispatch(removeAdsCampaignStatusOverrides(overridesToClear));
      }

      return data;
    },
    [dispatch]
  );

  const queuePlatformSync = useCallback(
    (campaignMap) => {
      const campaignsActive = [];
      Object.keys(campaignMap || {}).forEach((campaignID) => {
        const campaign = campaignMap[campaignID] || {};
        if (Object.keys(campaign).length <= 3) return;
        const statusCampaign = getStatusAdsCampaign({
          states: campaign.states,
          target: "general",
        });
        if (
          statusCampaign !== ADS_CAMPAIGN_STATUS_DRAFT &&
          !campaignsSyncedRef.current.includes(campaignID)
        ) {
          campaignsActive.push(campaignID);
        }
      });

      if (campaignsActive.length === 0) return;

      campaignsSyncedRef.current = [
        ...campaignsSyncedRef.current,
        ...campaignsActive,
      ];
      setCampaignsSynced(campaignsSyncedRef.current);
      setSyncingCampaignIDs((prev) => [
        ...new Set([...prev, ...campaignsActive]),
      ]);
      onUpdateAdsCampaignInformationFromPlatforms(campaignsActive).finally(
        () => {
          setSyncingCampaignIDs((prev) =>
            prev.filter((id) => !campaignsActive.includes(id))
          );
        }
      );
    },
    [onUpdateAdsCampaignInformationFromPlatforms]
  );

  const getInitialData = useCallback(async () => {
    if (isLoadingConfiguration || activeFolderID) return;
    setCampaigns(null);
    setIsLoading(true);
    setLastCampaignID(null);
    setLoadedLastCampaign(false);
    await dispatch(
      startGetAdsCampaigns({
        onChangeLastKey: (lastKey) => setLastCampaignID(lastKey),
        onLoadLastKey: () => setLoadedLastCampaign(true),
        sortConfig,
        subValueSort,
        onChangeSubValueSort: (nextSubValueSort) =>
          setSubValueSort(nextSubValueSort),
      })
    );
    setIsLoading(false);
  }, [
    activeFolderID,
    dispatch,
    isLoadingConfiguration,
    sortConfig,
    subValueSort,
  ]);

  const onGetNextCampaignsFromRTDB = useCallback(async () => {
    if (isSyncing || activeFolderID) return;

    setIsSyncing(true);
    await dispatch(
      startGetAdsCampaigns({
        lastKey: lastCampaignID,
        onChangeLastKey: (lastKey) => setLastCampaignID(lastKey),
        onLoadLastKey: () => setLoadedLastCampaign(true),
        sortConfig,
        subValueSort,
        onChangeSubValueSort: (nextSubValueSort) =>
          setSubValueSort(nextSubValueSort),
      })
    );
    setIsSyncing(false);
  }, [
    activeFolderID,
    dispatch,
    isSyncing,
    lastCampaignID,
    sortConfig,
    subValueSort,
  ]);

  const loadFolderCampaignPage = useCallback(
    async ({ pageIDs, append, requestID }) => {
      pageIDs.forEach((id) => loadedFolderIDsRef.current.add(id));
      const records = pageIDs.length
        ? await dispatch(
            startGetAdsCampaignRecordsByIDs({ campaignIDs: pageIDs })
          )
        : {};
      if (folderRequestRef.current !== requestID) return false;

      const nextCampaigns = applyOverrides(records || {});
      setCampaigns((prev) =>
        append ? { ...(prev || {}), ...nextCampaigns } : nextCampaigns
      );
      queuePlatformSync(nextCampaigns);
      return true;
    },
    [applyOverrides, dispatch, queuePlatformSync]
  );

  const onGetNextCampaigns = useCallback(async () => {
    if (!activeFolderID) {
      await onGetNextCampaignsFromRTDB();
      return;
    }

    if (isSyncing || loadedLastCampaign || isLoading) return;

    const remaining = (folderCampaignIDs || []).filter(
      (id) => !loadedFolderIDsRef.current.has(id)
    );
    const pageIDs = remaining.slice(0, ADS_CAMPAIGNS_QUERY_PAGE_SIZE);
    if (pageIDs.length === 0) {
      setLoadedLastCampaign(true);
      return;
    }

    const requestID = folderRequestRef.current;
    setIsSyncing(true);
    try {
      const applied = await loadFolderCampaignPage({
        pageIDs,
        append: true,
        requestID,
      });
      if (!applied) return;
      setLoadedLastCampaign(
        loadedFolderIDsRef.current.size >= (folderCampaignIDs || []).length
      );
    } finally {
      if (folderRequestRef.current === requestID) {
        setIsSyncing(false);
      }
    }
  }, [
    activeFolderID,
    folderCampaignIDs,
    isLoading,
    isSyncing,
    loadFolderCampaignPage,
    loadedLastCampaign,
    onGetNextCampaignsFromRTDB,
  ]);

  useEffect(() => {
    const loadSavedSortConfig = async () => {
      const response = await dispatch(startGetHomeMetricsConfiguration());
      if (!response?.ok) {
        setIsLoadingConfiguration(false);
        return;
      }

      const config = response?.data || {};

      const preset = config?.tablePreset;
      if (!preset) {
        setIsLoadingConfiguration(false);
      } else {
        const savedKey = preset?.sortableColumn;
        const savedDirection =
          preset?.sortDirection === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC;

        const isValidKey = Object.values(SORTABLE_COLUMNS).includes(savedKey);
        if (isValidKey) {
          setSortConfig({ key: savedKey, direction: savedDirection });
        }
      }

      const savedFilter = config.filterCampaingsByDates;
      const dateRangeValues = Object.values(DATE_RANGE);
      const isValidFilter = dateRangeValues.includes(savedFilter);

      if (!savedFilter || savedFilter === "none" || !isValidFilter) {
        setDatePresetKey(DATE_RANGE.last_14);
        setSavedDateRange(null);
      } else {
        setDatePresetKey(savedFilter);

        if (savedFilter === DATE_RANGE.custom) {
          const { startDate, endDate } = config?.customDates || {};
          const hasValidCustomDates =
            typeof startDate === "number" && typeof endDate === "number";

          setSavedDateRange(
            hasValidCustomDates
              ? {
                  startDateTime: new Date(startDate),
                  endDateTime: new Date(endDate),
                }
              : null
          );
        } else {
          setSavedDateRange(null);
        }
      }

      setIsLoadingConfiguration(false);
    };

    loadSavedSortConfig();
    // eslint-disable-next-line
  }, [userID]);

  const onTableSort = useCallback(
    (columnKey, columnVisibility = {}) => {
      setSortConfig((prevConfig) => {
        const newDirection =
          prevConfig.key === columnKey &&
          prevConfig.direction === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.ASC
            : SORT_DIRECTION.DESC;

        dispatch(
          startSetHomeMetricsConfiguration({
            metricsConfiguration: {
              ...columnVisibility,
              tablePreset: {
                sortableColumn: columnKey,
                sortDirection:
                  newDirection === SORT_DIRECTION.ASC ? "asc" : "desc",
              },
            },
          })
        );

        return { key: columnKey, direction: newDirection };
      });

      setSubValueSort(null);
    },
    [dispatch]
  );

  const onChangeDatePresetKey = useCallback(
    ({ presetKey: nextKey, startDate = null, endDate = null }) => {
      const dateRangeValues = Object.values(DATE_RANGE);
      const isValidFilter = dateRangeValues.includes(nextKey);

      const normalizedKey = !nextKey
        ? DATE_RANGE.last_14
        : isValidFilter
          ? nextKey
          : DATE_RANGE.last_14;

      setDatePresetKey(normalizedKey);

      const hasValidCustomDates =
        normalizedKey === DATE_RANGE.custom &&
        typeof startDate === "number" &&
        typeof endDate === "number";

      if (hasValidCustomDates) {
        setSavedDateRange({
          startDateTime: new Date(startDate),
          endDateTime: new Date(endDate),
        });
      } else {
        setSavedDateRange(null);
      }

      const metricsConfiguration =
        normalizedKey === DATE_RANGE.custom && hasValidCustomDates
          ? {
              filterCampaingsByDates: DATE_RANGE.custom,
              customDates: { startDate, endDate },
            }
          : {
              filterCampaingsByDates: normalizedKey,
              customDates: null,
            };

      dispatch(
        startSetHomeMetricsConfiguration({
          metricsConfiguration,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (activeFolderID) return;
    getInitialData();
    // eslint-disable-next-line
  }, [userID, isLoadingConfiguration, activeFolderID]);

  useEffect(() => {
    if (activeFolderID) return;
    if (campaigns === null) return;

    getInitialData();
    // eslint-disable-next-line
  }, [sortConfig.key, sortConfig.direction, activeFolderID]);

  useEffect(() => {
    if (!activeFolderID || isLoadingConfiguration) return undefined;

    const requestID = folderRequestRef.current + 1;
    folderRequestRef.current = requestID;
    loadedFolderIDsRef.current = new Set();
    setIsLoading(true);
    setCampaigns(null);
    setLoadedLastCampaign(false);
    setFolderIndex({ folderID: activeFolderID, ids: [] });

    const loadFolder = async () => {
      const ids = await dispatch(
        startGetAdsCampaignFolderContentIDs({ folderID: activeFolderID })
      );
      if (folderRequestRef.current !== requestID) return;

      const folderIDs = Array.isArray(ids) ? ids : [];
      setFolderIndex({ folderID: activeFolderID, ids: folderIDs });
      const pageIDs = folderIDs.slice(0, ADS_CAMPAIGNS_QUERY_PAGE_SIZE);
      const applied = await loadFolderCampaignPage({
        pageIDs,
        append: false,
        requestID,
      });
      if (!applied) return;
      setLoadedLastCampaign(pageIDs.length >= folderIDs.length);
      setIsLoading(false);
    };

    loadFolder();
    return () => {
      folderRequestRef.current += 1;
    };
    // eslint-disable-next-line
  }, [activeFolderID, isLoadingConfiguration, userID]);

  useEffect(
    () => {
      if (activeFolderID) return;

      const data = {};
      const campaignsActive = [];
      const overridesToClear = [];

      Object.keys(adsCampaigns).forEach((campaignID) => {
        const campaign = adsCampaigns[campaignID] || {};
        const keys = Object.keys(campaign);

        if (keys.length <= 3) return;

        const statusCampaign = getStatusAdsCampaign({
          states: campaign.states,
          target: "general",
        });

        const overrideStatus = statusOverrides[campaignID];
        data[campaignID] = overrideStatus
          ? {
              ...campaign,
              states: {
                ...campaign.states,
                general: overrideStatus,
              },
            }
          : campaign;

        if (overrideStatus && overrideStatus === statusCampaign) {
          overridesToClear.push(campaignID);
        }

        if (
          statusCampaign !== ADS_CAMPAIGN_STATUS_DRAFT &&
          !campaignsSynced.includes(campaignID)
        )
          campaignsActive.push(campaignID);
      });

      if (campaignsActive.length > 0) {
        setCampaignsSynced((prev) => [...prev, ...campaignsActive]);
        setSyncingCampaignIDs((prev) => [
          ...new Set([...prev, ...campaignsActive]),
        ]);
        onUpdateAdsCampaignInformationFromPlatforms(campaignsActive).finally(
          () => {
            setSyncingCampaignIDs((prev) =>
              prev.filter((id) => !campaignsActive.includes(id))
            );
          }
        );
      }

      if (overridesToClear.length > 0) {
        dispatch(removeAdsCampaignStatusOverrides(overridesToClear));
      }

      setCampaigns(data);
    },
    // eslint-disable-next-line
    [adsCampaigns, activeFolderID]
  );

  const noteFolderMembership = useCallback(
    ({ campaignIDs, folderID: nextFolderID }) => {
      if (!activeFolderID) return;
      const normalizedIDs = (campaignIDs || []).map(String).filter(Boolean);
      const staysInFolder =
        String(nextFolderID || "") === String(activeFolderID);

      if (staysInFolder) {
        normalizedIDs.forEach((id) => loadedFolderIDsRef.current.add(id));
      } else {
        normalizedIDs.forEach((id) => loadedFolderIDsRef.current.delete(id));
      }

      setFolderIndex((current) => {
        if (!current || current.folderID !== activeFolderID) return current;
        if (staysInFolder) {
          return {
            folderID: activeFolderID,
            ids: [...new Set([...current.ids, ...normalizedIDs])],
          };
        }
        const removedIDs = new Set(normalizedIDs);
        return {
          folderID: activeFolderID,
          ids: current.ids.filter((id) => !removedIDs.has(id)),
        };
      });
    },
    [activeFolderID]
  );

  useEffect(() => {
    if (!activeFolderID) {
      folderCampaignsBaselineRef.current = adsCampaigns;
      return;
    }

    const previousCampaigns = folderCampaignsBaselineRef.current;
    folderCampaignsBaselineRef.current = adsCampaigns;
    if (previousCampaigns === adsCampaigns) return;

    setCampaigns((current) => {
      if (!current) return current;
      let changed = false;
      const next = { ...current };

      Object.keys(current).forEach((campaignID) => {
        const reduxCampaign = adsCampaigns[campaignID];
        const previousCampaign = previousCampaigns?.[campaignID];
        if (!reduxCampaign || reduxCampaign === previousCampaign) return;
        if (Object.keys(reduxCampaign).length <= 3) return;

        const localCampaign = current[campaignID];
        const merged = {
          ...localCampaign,
          ...reduxCampaign,
        };
        if (Object.prototype.hasOwnProperty.call(localCampaign, "folderID")) {
          merged.folderID = localCampaign.folderID;
        } else {
          delete merged.folderID;
        }
        const overrideStatus = statusOverridesRef.current?.[campaignID];
        if (overrideStatus) {
          merged.states = {
            ...merged.states,
            general: overrideStatus,
          };
        }
        next[campaignID] = merged;
        changed = true;
      });

      return changed ? next : current;
    });
  }, [activeFolderID, adsCampaigns]);

  useEffect(() => {
    const successCampaigns = publishingCampaigns
      .filter((campaign) => campaign.status === ALERTS_ASYNC_STATUS.SUCCESS)
      .map((campaign) => campaign.campaignID);

    if (successCampaigns.length > 0) {
      successCampaigns.forEach((campaignID) => {
        dispatch(startGetAdsCampaign(campaignID, true));
      });
    }
    // eslint-disable-next-line
  }, [publishingCampaigns]);

  return {
    campaigns,
    onChangeCampaigns: setCampaigns,
    isLoadingConfiguration,
    isLoadingCampaigns: isLoading,
    sortConfig,
    onTableSort,
    lastCampaignID,
    loadedLastCampaign,
    onGetNextCampaigns,
    isSyncing,
    syncingCampaignIDs,
    datePresetKey,
    onChangeDatePresetKey,
    savedDateRange,
    folderCampaignIDs,
    noteFolderMembership,
  };
};

export default useAdsCampaignsList;
